import * as Headless from "@headlessui/react";
import React from "react";

import { cn } from "@jobenvoy/utils";

/**
 * A modal over the page. The surface is the app's card: the colours come
 * from the `--color-card`, `--color-line` and `--color-fg` family every app
 * here declares in its stylesheet, so the dialog looks like the page it is
 * over without carrying a palette of its own.
 */
const sizes = {
    xs: "max-w-xs",
    sm: "max-w-sm",
    md: "max-w-md",
    lg: "max-w-lg",
    xl: "max-w-xl",
    "2xl": "max-w-2xl",
    "3xl": "max-w-3xl",
    "4xl": "max-w-4xl",
    "5xl": "max-w-5xl",
} as const;

/**
 * How long after opening a pointer close is taken for the rest of the
 * click that opened it: a double click on an opener lands its second
 * click on the scrim, which would close the dialog it just opened. The
 * same holds after `graceKey` changes: a step that shrinks the panel
 * leaves the rest of a fast press on the scrim. Each press the grace
 * holds back starts it again, so a run of fast presses never closes it.
 */
export const OPENING_GRACE_MS = 400;

export interface DialogProps extends Omit<Headless.DialogProps, "as" | "className"> {
    size?: keyof typeof sizes;
    className?: string;
    /** Merged over the scrim's own classes, for an app with a scrim of its own. */
    backdropClassName?: string;
    /** Merged over the scrolling frame's padding and the row that centres the panel. */
    frameClassName?: string;
    rowClassName?: string;
    /** The panel's inline style, for a width that is not one of `sizes`. */
    panelStyle?: React.CSSProperties;
    /** Changes with what the panel shows, such as a step; each change starts the grace again. */
    graceKey?: string;
    children: React.ReactNode;
}

export function Dialog({
    size = "lg",
    className,
    backdropClassName,
    frameClassName,
    rowClassName,
    panelStyle,
    graceKey,
    children,
    open,
    onClose,
    ...props
}: DialogProps) {
    const openedAt = React.useRef(0);
    React.useEffect(() => {
        if (open) openedAt.current = performance.now();
    }, [open, graceKey]);
    return (
        <Headless.Dialog
            {...props}
            open={open}
            onClose={(value) => {
                // Escape always closes; a click on the scrim waits out the grace.
                const key =
                    typeof KeyboardEvent !== "undefined" && window.event instanceof KeyboardEvent;
                const now = performance.now();
                if (!key && now - openedAt.current < OPENING_GRACE_MS) {
                    openedAt.current = now;
                    return;
                }
                onClose(value);
            }}
        >
            <Headless.DialogBackdrop
                transition
                className={cn(
                    "fixed inset-0 bg-black/40 backdrop-blur-sm",
                    "transition-opacity duration-200 data-closed:opacity-0",
                    backdropClassName,
                )}
            />
            <div className={cn("fixed inset-0 overflow-y-auto p-4 sm:p-8", frameClassName)}>
                <div
                    className={cn(
                        "flex min-h-full items-end justify-center sm:items-center",
                        rowClassName,
                    )}
                >
                    <Headless.DialogPanel
                        transition
                        style={panelStyle}
                        className={cn(
                            sizes[size],
                            "w-full rounded border border-line bg-card px-4 py-4 text-fg",
                            "transition-all duration-200",
                            "data-closed:translate-y-4 data-closed:opacity-0",
                            "sm:data-closed:translate-y-0 sm:data-closed:scale-95",
                            className,
                        )}
                    >
                        {children}
                    </Headless.DialogPanel>
                </div>
            </div>
        </Headless.Dialog>
    );
}

export interface DialogTitleProps extends Omit<Headless.DialogTitleProps, "as" | "className"> {
    className?: string;
}

export function DialogTitle({ className, ...props }: DialogTitleProps) {
    return (
        <Headless.DialogTitle
            {...props}
            className={cn(
                "font-display text-2xl font-semibold leading-normal tracking-tight text-balance sm:text-3xl",
                className,
            )}
        />
    );
}

export interface DialogDescriptionProps extends Omit<
    Headless.DescriptionProps<"p">,
    "as" | "className"
> {
    className?: string;
}

export function DialogDescription({ className, ...props }: DialogDescriptionProps) {
    return (
        <Headless.Description
            as="p"
            {...props}
            className={cn(
                "mt-2 font-sans text-base leading-6 text-pretty text-fg-muted",
                className,
            )}
        />
    );
}

export function DialogBody({ className, ...props }: React.ComponentPropsWithoutRef<"div">) {
    return <div {...props} className={cn("mt-6", className)} />;
}

export function DialogActions({ className, ...props }: React.ComponentPropsWithoutRef<"div">) {
    return (
        <div
            {...props}
            className={cn(
                "mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end *:w-full sm:*:w-auto",
                className,
            )}
        />
    );
}
